/**
 * Very-big waving crowd flags (runtime-handoff-v5.2, anime-stadium export).
 *
 * `stadium_flags_verybig.glb` ships FOUR SOURCE MESHES only (one per club colour)
 * — unlike core/roof/seats/props it is NOT pre-placed geometry. The 92 placements
 * (position/yaw/scale/colour per flag) live in `flag_verybig_placements.bin`, a
 * companion binary the loader plan does not fetch (its metadata sibling carries no
 * per-instance data, so a plain JSON round-trip through StadiumV5Loader can't
 * represent it — fetched directly here, the same way `wireCrowd()` fetches
 * `crowd_static_01.glb` outside the byte-tracked plan).
 *
 * Geometry note (from the export): each flag is modelled with the pole at local
 * origin, the cloth running along local +X away from the pole, height in local
 * +Y, and the ripple in local +Z. The wave shader below displaces Z as a function
 * of X, tapered to zero at the pole so the cloth stays attached — ported from the
 * export's own `web/flags.js` reference module, unchanged, because it was
 * calibrated against this exact mesh convention.
 */
import {
  BufferAttribute, Color, Euler, InstancedBufferAttribute, InstancedMesh, Matrix4, Object3D,
  Quaternion, Vector3, type BufferGeometry, type Material, type Mesh,
} from 'three';

/** One placed flag: metres + a yaw (radians) about world-Y (already Y-up). */
export interface FlagInstance {
  x: number;
  y: number;
  z: number;
  rotY: number;
  scale: number;
  variantIndex: number;
}

export interface FlagPlacements {
  variants: string[];
  instances: FlagInstance[];
}

const FLOATS_PER_INSTANCE = 6;

/**
 * Parse the metadata JSON (`flag_verybig_placements.json`) + the raw instance
 * binary (`flag_verybig_placements.bin`, float32 x6: posX,posY,posZ,rotZ,scale,
 * variantIndex, Blender Z-up) into typed, Y-up instances. Throws (fail loud) on
 * a format this module has not been proven against, or a count mismatch between
 * the two files — silently mis-placing or mis-colouring a club flag is worse than
 * refusing to guess.
 */
export function parseFlagVeryBigPlacements(meta: unknown, bin: ArrayBuffer): FlagPlacements {
  const root = meta as { format?: string; count?: number; upAxis?: string; variants?: string[] };
  if (!root.format?.startsWith('float32 x6')) {
    throw new Error(`flagVeryBigPlacements: unexpected format "${root.format ?? ''}" — this parser is proven only for "float32 x6: posX,posY,posZ,rotZ,scale,variantIndex".`);
  }
  if (!root.upAxis?.startsWith('Z')) {
    throw new Error(`flagVeryBigPlacements: unexpected upAxis "${root.upAxis ?? ''}" — this parser converts FROM Blender Z-up; a Y-up source would be double-converted.`);
  }
  const variants = root.variants ?? [];
  if (variants.length === 0) throw new Error('flagVeryBigPlacements: no variants listed in the metadata JSON');

  const src = new Float32Array(bin);
  const count = Math.floor(src.length / FLOATS_PER_INSTANCE);
  if (root.count !== undefined && root.count !== count) {
    throw new Error(`flagVeryBigPlacements: metadata count ${root.count} does not match .bin length ${count}`);
  }

  const instances: FlagInstance[] = new Array(count);
  for (let i = 0; i < count; i++) {
    const o = i * FLOATS_PER_INSTANCE;
    const bx = src[o]!, by = src[o + 1]!, bz = src[o + 2]!;
    // Blender Z-up -> three.js Y-up: (x, z, -y) — same convention as crowd/seat data.
    instances[i] = {
      x: bx, y: bz, z: -by,
      rotY: src[o + 3]!,
      scale: src[o + 4]!,
      variantIndex: Math.round(src[o + 5]!),
    };
  }
  return { variants, instances };
}

const WAVE_CHUNK = /* glsl */ `
  float fx = clamp(position.x / uFlagLength, 0.0, 1.0);
  float taper = fx * fx * (3.0 - 2.0 * fx);          // 0 at the pole, 1 at the tip
  float t = uFlagTime * uFlagSpeed + iPhase;
  float w = sin(position.x * uFlagWaves - t) * uFlagAmp * taper;
  float w2 = sin(position.x * uFlagWaves * 0.5 - t * 0.7 + 1.3) * uFlagAmp * 0.45 * taper;
  transformed.z += w + w2;
  transformed.x -= abs(w) * 0.10;                    // cloth pulls in as it ripples
  transformed.y += w * 0.06;                         // slight lift on the crest
`;

interface WaveUniforms {
  uFlagTime: { value: number };
  uFlagSpeed: { value: number };
  uFlagAmp: { value: number };
  uFlagWaves: { value: number };
  uFlagLength: { value: number };
}

/**
 * Approved club-colour palette (§9/§16 — muted, believable, never neon), keyed
 * by the export's variant NAME. The GLB's 4 cloth meshes all share ONE material
 * (`GX_FlagPole`, flat grey, no vertex colours and no texture), because the
 * export's metadata says to "recolour at runtime rather than shipping more
 * variants".
 */
const FLAG_TINT_BY_VARIANT: Record<string, number> = {
  FLAG_VB_Red: 0x9e2630,
  FLAG_VB_Yellow: 0xd0a428,
  FLAG_VB_Blue: 0x27538f,
  FLAG_VB_White: 0xdedcd4,
};

/** The pole's own grey, kept off the club colour (source `GX_FlagPole` base). */
const FLAG_POLE_COLOR = 0xb8bcc2;

/**
 * Local +X below which a vertex belongs to the POLE rather than the cloth.
 *
 * One material covers both, so tinting the material tinted the pole too and the
 * flags read as flat floating rectangles with no visible staff. Measured from
 * the GLB: the pole occupies x≈[-0.04, 0.10] and the cloth runs x≈0.1→3.20, so
 * anything under this threshold is pole.
 */
const FLAG_POLE_X = 0.12;

/**
 * Split pole from cloth with baked VERTEX COLOURS — the only separation
 * available, since the two share a material and a primitive. The material then
 * carries white and `vertexColors`, so the pole stays grey while the cloth takes
 * the club colour.
 */
function bakeFlagColors(geometry: BufferGeometry, clothHex: number): BufferGeometry {
  const g = geometry.clone();
  const pos = g.getAttribute('position');
  const cloth = new Color(clothHex);
  const pole = new Color(FLAG_POLE_COLOR);
  const colors = new Float32Array(pos.count * 3);
  for (let i = 0; i < pos.count; i++) {
    const c = pos.getX(i) < FLAG_POLE_X ? pole : cloth;
    colors[i * 3] = c.r;
    colors[i * 3 + 1] = c.g;
    colors[i * 3 + 2] = c.b;
  }
  g.setAttribute('color', new BufferAttribute(colors, 3));
  return g;
}

/** Wrap the source material so it waves and reads its colour per-vertex. */
function makeWaving(material: Material, uniforms: WaveUniforms): Material {
  const m = material.clone();
  const std = m as unknown as { color?: Color; vertexColors: boolean };
  std.vertexColors = true;
  // White base so the baked vertex colour is what actually shows.
  std.color?.setHex(0xffffff);
  m.onBeforeCompile = (shader) => {
    shader.uniforms.uFlagTime = uniforms.uFlagTime;
    shader.uniforms.uFlagSpeed = uniforms.uFlagSpeed;
    shader.uniforms.uFlagAmp = uniforms.uFlagAmp;
    shader.uniforms.uFlagWaves = uniforms.uFlagWaves;
    shader.uniforms.uFlagLength = uniforms.uFlagLength;
    shader.vertexShader = shader.vertexShader
      .replace(
        '#include <common>',
        `#include <common>
         attribute float iPhase;
         uniform float uFlagTime, uFlagSpeed, uFlagAmp, uFlagWaves, uFlagLength;`,
      )
      .replace('#include <begin_vertex>', `#include <begin_vertex>\n${WAVE_CHUNK}`);
  };
  m.needsUpdate = true;
  return m;
}

const mat4 = new Matrix4();
const quat = new Quaternion();
const euler = new Euler();
const pos = new Vector3();
const scl = new Vector3();

/**
 * Instances the 4 club-colour source meshes at their 92 placements, one
 * InstancedMesh per variant actually used, with the wave running in the vertex
 * shader (zero per-frame CPU cost beyond updating one uniform).
 */
export class FlagsSystem {
  readonly root = new Object3D();
  private meshes: InstancedMesh[] = [];
  private readonly uniforms: WaveUniforms = {
    uFlagTime: { value: 0 },
    uFlagSpeed: { value: 2.6 },
    uFlagAmp: { value: 0.30 },
    uFlagWaves: { value: 2.1 },
    uFlagLength: { value: 3.2 }, // metres — must match the export's cloth length
  };

  constructor() { this.root.name = 'FlagsVeryBigSystem'; }

  /**
   * @param sourceMeshes the loaded `stadium_flags_verybig.glb` scene (4 meshes,
   *   one per `placements.variants` entry, matched by name substring since glTF
   *   mesh names don't always equal the Blender object name)
   */
  build(sourceMeshes: Object3D, placements: FlagPlacements): void {
    this.dispose();

    const byName = new Map<string, Mesh>();
    sourceMeshes.traverse((n) => {
      const mesh = n as Mesh;
      if ((mesh as unknown as { isMesh?: boolean }).isMesh) byName.set(mesh.name, mesh);
    });
    const meshForVariant = (variant: string): Mesh => {
      for (const [name, mesh] of byName) if (name.includes(variant)) return mesh;
      throw new Error(
        `FlagsSystem: no mesh found for variant "${variant}" in stadium_flags_verybig.glb ` +
          `(have: ${[...byName.keys()].join(', ') || '(none)'})`,
      );
    };

    const buckets = new Map<number, FlagInstance[]>();
    for (const inst of placements.instances) {
      const list = buckets.get(inst.variantIndex) ?? [];
      list.push(inst);
      buckets.set(inst.variantIndex, list);
    }

    for (const [variantIndex, list] of buckets) {
      const variantName = placements.variants[variantIndex];
      if (variantName === undefined) {
        throw new Error(`FlagsSystem: placement references variantIndex ${variantIndex}, outside variants[] (length ${placements.variants.length})`);
      }
      const srcMesh = meshForVariant(variantName);
      const srcMat = Array.isArray(srcMesh.material) ? srcMesh.material[0]! : srcMesh.material;
      const clothHex = FLAG_TINT_BY_VARIANT[variantName];
      if (clothHex === undefined) {
        console.warn(`FlagsSystem: no club colour for variant "${variantName}" — it will render pole-grey`);
      }
      const geometry = bakeFlagColors(srcMesh.geometry, clothHex ?? FLAG_POLE_COLOR);

      const inst = new InstancedMesh(geometry, makeWaving(srcMat, this.uniforms), list.length);
      inst.name = `FlagsVeryBig_${variantName}`;

      const phase = new Float32Array(list.length);
      list.forEach((f, k) => {
        pos.set(f.x, f.y, f.z);
        euler.set(0, f.rotY, 0);
        quat.setFromEuler(euler);
        scl.set(f.scale, f.scale, f.scale);
        inst.setMatrixAt(k, mat4.compose(pos, quat, scl));
        // Decorrelated per-instance phase so 92 flags don't ripple in lockstep.
        phase[k] = ((k * 0.6180339887) % 1) * Math.PI * 2;
      });
      inst.instanceMatrix.needsUpdate = true;
      inst.geometry.setAttribute('iPhase', new InstancedBufferAttribute(phase, 1));
      inst.frustumCulled = false; // 92 instances spread around the whole bowl
      this.meshes.push(inst);
      this.root.add(inst);
    }
  }

  /** Call once per frame with `RenderState.elapsed` — drives the wave, nothing else. */
  update(elapsedS: number): void {
    this.uniforms.uFlagTime.value = elapsedS;
  }

  dispose(): void {
    for (const mesh of this.meshes) {
      this.root.remove(mesh);
      mesh.geometry.dispose();
      mesh.dispose();
    }
    this.meshes = [];
  }
}
